import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { SectionHeading } from '../components/SectionHeading';
import { ProjectCard } from '../components/ProjectCard';
import { CTABanner } from '../components/CTABanner';
interface ProjectDetails {
  id: number;
  title: string;
  client: string;
  industry: string;
  summary: string;
  problem: string;
  solution: string;
  impact: string;
}
export function ProjectsPage() {
  const [selectedProject, setSelectedProject] = useState<ProjectDetails | null>(null);
  const projects: ProjectDetails[] = [{
    id: 1,
    title: 'IT Equipment Supply & Support',
    client: 'Hatlab Cream Delight',
    industry: 'Digital Inclusion',
    summary: 'Supply, installation and technical support of computer systems and accessories for business operations.',
    problem: 'The client needed reliable computer systems and accessories to support their growing business operations, but lacked the technical expertise for proper setup and ongoing maintenance.',
    solution: 'Invigo provided end-to-end supply, installation, and technical support of computer systems and accessories. Our team ensured proper configuration, network setup, and provided training for staff on the new equipment.',
    impact: 'Streamlined business operations with reliable IT infrastructure, reduced downtime through proactive technical support, and improved staff productivity with modern computing equipment.'
  }, {
    id: 2,
    title: 'Recreational Facility IT Setup',
    client: 'Maitama Amusement Park',
    industry: 'Digital Inclusion',
    summary: 'Complete supply, installation and technical support of computer systems and accessories.',
    problem: 'The amusement park required a comprehensive IT setup to manage ticketing, operations, and customer services but had no existing digital infrastructure in place.',
    solution: "Delivered and installed a complete suite of computer systems and accessories. Provided technical support to ensure seamless integration with the park's operational workflows and customer-facing services.",
    impact: 'Enabled digital ticketing and operations management, improved customer service delivery, and established a reliable IT foundation for future expansion.'
  }, {
    id: 3,
    title: 'Enterprise IT Infrastructure',
    client: 'Subotech Nig. Ltd',
    industry: 'Digital Inclusion',
    summary: 'Supply, installation and technical support of computer systems and accessories for enterprise operations.',
    problem: 'Subotech needed to modernize their IT infrastructure to keep pace with business growth and improve operational efficiency across their organization.',
    solution: "Invigo supplied, installed, and provided ongoing technical support for a comprehensive suite of computer systems and accessories tailored to the client's specific business requirements.",
    impact: 'Enhanced operational efficiency through modern IT systems, reduced equipment downtime with dedicated technical support, and positioned the company for scalable growth.'
  }, {
    id: 4,
    title: 'Laptop Ownership Scheme',
    client: 'Niger State Government',
    industry: 'Government',
    summary: 'Procurement and delivery of 1,100 laptops for the Niger State Ownership Scheme.',
    problem: 'The Niger State Government launched a digital inclusion initiative requiring the procurement and distribution of laptops to bridge the digital divide among citizens and government workers.',
    solution: 'Invigo managed the end-to-end procurement and delivery of 1,100 laptops for the Niger State Ownership Scheme. This included vendor negotiation, quality assurance, logistics coordination, and delivery to designated distribution points.',
    impact: 'Successfully delivered 1,100 laptops, empowering citizens and government workers with access to digital tools. The scheme significantly advanced digital literacy and inclusion across Niger State.'
  }, {
    id: 5,
    title: 'Broadcast Platform Feasibility Study',
    client: 'DFID',
    industry: 'Business Innovation',
    summary: 'Feasibility study of broadcast platform (radio) users documentation and analysis.',
    problem: 'DFID required a comprehensive feasibility study to understand the viability and user landscape of broadcast radio platforms for development communication in Nigeria.',
    solution: 'Conducted an in-depth feasibility study of broadcast platform (radio) users, including comprehensive documentation and analysis of user demographics, reach, and engagement patterns to inform strategic decision-making.',
    impact: "Delivered actionable insights that informed DFID's communication strategy for development programs, enabling more effective outreach through broadcast media channels."
  }, {
    id: 6,
    title: 'Converged Enterprise IP Network',
    client: 'SPDC',
    industry: 'Oil & Gas',
    summary: 'Design and implementation of converged enterprise IP on fibre to home network for residential estates in Abuja.',
    problem: "SPDC's residential estates in Abuja lacked modern converged communication infrastructure, requiring separate systems for voice, data, and surveillance.",
    solution: 'Designed and implemented a converged Enterprise IP solution integrating VOIP, Data, and CCTV Surveillance on a Fibre to Home Network for residential estates in Abuja. The solution unified all communication services onto a single high-speed fibre infrastructure.',
    impact: 'Delivered a unified, high-speed communication infrastructure combining voice, data, and security surveillance. Residents now enjoy seamless connectivity and enhanced security through integrated CCTV monitoring.'
  }];
  return <main className="w-full pt-20" data-id="element-447">
      {/* Page Header */}
      <section className="bg-navy-900 py-20 relative overflow-hidden" data-id="element-448">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5" data-id="element-449" />
        <div className="max-w-7xl mx-auto px-4 relative z-10" data-id="element-450">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-6xl font-extrabold text-white mb-6" data-id="element-451">
            Case <span className="text-tech-green" data-id="element-452">Studies</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-slate-300 max-w-2xl" data-id="element-453">
            Explore how we've helped organizations across Africa overcome
            complex technological challenges and achieve their strategic goals.
          </motion.p>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-24 bg-slate-50" data-id="element-454">
        <div className="max-w-7xl mx-auto px-4" data-id="element-455">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-456">
            {projects.map((project, idx) => <ProjectCard key={project.id} title={project.title} client={project.client} industry={project.industry} summary={project.summary} delay={idx * 0.1} onClick={() => setSelectedProject(project)} data-id="element-457" />)}
          </div>
        </div>
      </section>

      {/* Project Details Modal */}
      <AnimatePresence data-id="element-458">
        {selectedProject && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-459">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 bg-navy-900/80 backdrop-blur-sm" onClick={() => setSelectedProject(null)} data-id="element-460" />

            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="relative w-full max-w-3xl bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-461">
              {/* Modal Header */}
              <div className="bg-navy-900 p-6 sm:p-8 text-white relative shrink-0" data-id="element-462">
                <button onClick={() => setSelectedProject(null)} className="absolute top-6 right-6 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors" data-id="element-463">
                  <X className="w-6 h-6" data-id="element-464" />
                </button>

                <span className="inline-block px-3 py-1 text-xs font-semibold bg-tech-green/20 text-tech-green rounded-full border border-tech-green/30 mb-4" data-id="element-465">
                  {selectedProject.industry}
                </span>
                <h2 className="text-2xl sm:text-3xl font-bold mb-2 pr-12" data-id="element-466">
                  {selectedProject.title}
                </h2>
                <p className="text-slate-300 font-medium" data-id="element-467">
                  Client: {selectedProject.client}
                </p>
              </div>

              {/* Modal Body */}
              <div className="p-6 sm:p-8 overflow-y-auto" data-id="element-468">
                <div className="space-y-8" data-id="element-469">
                  <div data-id="element-470">
                    <h3 className="text-lg font-bold text-navy-900 mb-3 flex items-center gap-2" data-id="element-471">
                      <span className="w-8 h-8 rounded-lg bg-red-100 text-red-600 flex items-center justify-center text-sm" data-id="element-472">
                        01
                      </span>
                      The Challenge
                    </h3>
                    <p className="text-slate-600 leading-relaxed" data-id="element-473">
                      {selectedProject.problem}
                    </p>
                  </div>

                  <div data-id="element-474">
                    <h3 className="text-lg font-bold text-navy-900 mb-3 flex items-center gap-2" data-id="element-475">
                      <span className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center text-sm" data-id="element-476">
                        02
                      </span>
                      Our Solution
                    </h3>
                    <p className="text-slate-600 leading-relaxed" data-id="element-477">
                      {selectedProject.solution}
                    </p>
                  </div>

                  <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100" data-id="element-478">
                    <h3 className="text-lg font-bold text-navy-900 mb-3 flex items-center gap-2" data-id="element-479">
                      <span className="w-8 h-8 rounded-lg bg-green-100 text-green-600 flex items-center justify-center text-sm" data-id="element-480">
                        03
                      </span>
                      The Impact
                    </h3>
                    <p className="text-slate-700 font-medium leading-relaxed" data-id="element-481">
                      {selectedProject.impact}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      <CTABanner data-id="element-482" />
    </main>;
}